import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2, Layers, Lock } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import api, { formatApiError } from "@/lib/api";

const fieldClass = "w-full rounded-lg bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const idSlug = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "").slice(0, 40) || `tier_${Math.random().toString(36).slice(2, 6)}`;

export default function AdminTiers() {
  const [tiers, setTiers] = useState(null);
  const [groups, setGroups] = useState([]);
  const [capKeys, setCapKeys] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/config/tiers").then(({ data }) => {
      setTiers(data.tiers || []);
      setGroups(data.cap_groups || []);
      setCapKeys(data.cap_keys || []);
    }).catch(() => setTiers(false));
  }, []);

  if (tiers === null) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary-c" /></div>;
  if (tiers === false) return <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load tiers.</p>;

  const setTier = (i, patch) => setTiers((ts) => ts.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const setCap = (i, cap, val) => setTiers((ts) => ts.map((t, idx) => (idx === i ? { ...t, capabilities: { ...t.capabilities, [cap]: val } } : t)));
  const setGroup = (i, groupCaps, val) => {
    setTiers((ts) => ts.map((t, idx) => {
      if (idx !== i) return t;
      const caps = { ...t.capabilities };
      groupCaps.forEach((c) => { caps[c.id] = val; });
      return { ...t, capabilities: caps };
    }));
  };
  const addTier = () => setTiers((ts) => [...ts, { id: idSlug(`tier ${ts.length + 1}`), name: `New Tier`, builtin: false, capabilities: Object.fromEntries(capKeys.map((c) => [c, false])) }]);
  const removeTier = (i) => setTiers((ts) => ts.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/tiers", {
        tiers: tiers.map((t) => ({ id: t.builtin ? t.id : idSlug(t.id || t.name), name: t.name, capabilities: t.capabilities })),
      });
      setTiers(data.tiers || []);
      toast.success("Tiers & access saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-tiers">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2"><Layers className="w-5 h-5 text-primary-c" /> Tiers & access control</h3>
          <p className="text-sm mt-1" style={{ color: "var(--text-2)" }}>Create membership tiers and toggle each feature they unlock. Built-in tiers can be edited but not removed.</p>
        </div>
        <button data-testid="tier-add" onClick={addTier} className="glass rounded-full px-4 py-2 text-sm font-bold flex items-center gap-2" style={{ color: "var(--text)" }}>
          <Plus className="w-4 h-4 text-primary-c" /> Add tier
        </button>
      </div>

      <div className="grid gap-4">
        {tiers.map((t, i) => (
          <div key={i} data-testid={`tier-card-${t.id}`} className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }}>
            <div className="flex flex-wrap items-center gap-3 justify-between mb-4">
              <div className="flex items-center gap-3 flex-1 min-w-[240px]">
                <input data-testid={`tier-name-${t.id}`} value={t.name} onChange={(e) => setTier(i, { name: e.target.value })}
                  className={`${fieldClass} font-display font-bold max-w-[260px]`} style={{ color: "var(--text)" }} />
                <span className="mono text-[10px] uppercase tracking-wider glass rounded-full px-2 py-0.5 flex items-center gap-1" style={{ color: "var(--text-2)" }}>
                  {t.builtin ? <><Lock className="w-3 h-3" /> built-in · {t.id}</> : `custom · ${t.id}`}
                </span>
              </div>
              {!t.builtin && (
                <button data-testid={`tier-remove-${t.id}`} onClick={() => removeTier(i)} aria-label="Remove tier"
                  className="glass rounded-lg w-9 h-9 flex items-center justify-center hover:scale-105 transition-transform">
                  <Trash2 className="w-4 h-4" style={{ color: "#ef4444" }} />
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {groups.map((g) => {
                const allOn = g.caps.every((c) => !!t.capabilities?.[c.id]);
                return (
                  <div key={g.id} data-testid={`tier-group-${t.id}-${g.id}`} className="rounded-xl glow-border p-4" style={{ background: "var(--bg)" }}>
                    <div className="flex items-center justify-between gap-2 mb-3">
                      <span className="font-display font-bold text-sm">{g.label}</span>
                      <button data-testid={`tier-group-toggle-${t.id}-${g.id}`} onClick={() => setGroup(i, g.caps, !allOn)}
                        className="mono text-[10px] uppercase tracking-wider text-primary-c hover:underline">{allOn ? "None" : "All"}</button>
                    </div>
                    <div className="space-y-2">
                      {g.caps.map((c) => (
                        <label key={c.id} data-testid={`tier-cap-${t.id}-${c.id}`} className="flex items-center justify-between gap-3 rounded-lg glass px-3 py-2">
                          <span className="text-xs leading-snug">{c.label}</span>
                          <Switch checked={!!t.capabilities?.[c.id]} onCheckedChange={(v) => setCap(i, c.id, v)} />
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <button data-testid="tiers-save" onClick={save} disabled={busy}
        className="btn-glow rounded-full px-6 py-3 font-bold inline-flex items-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save tiers & access
      </button>
    </div>
  );
}
